import { FC, FormEvent, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useAppContext, useSafeState } from '#hooks';
import { dashboardApiV3 } from '#api';
import { AccountPaymentMode, AccountType, B2bAccount, UserV3 } from '#types';

interface EditB2bProps {
    accountToEdit: Partial<B2bAccount>;
    setAccountToEdit: Dispatch<SetStateAction<B2bAccount | null>>;
    setIsExpandedBubble?: Dispatch<SetStateAction<boolean>>;
}

export const EditB2bAccount: FC<EditB2bProps> = ({ accountToEdit, setAccountToEdit, setIsExpandedBubble }) => {
    const { setApiStatus } = useAppContext();
    const [updatedAccount, setUpdatedAccount] = useSafeState<Partial<B2bAccount>>(accountToEdit);
    const [email, setEmail] = useSafeState<string>(accountToEdit.owner?.email || '');
    const creatingNewAccount = accountToEdit.nanoId === '';

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setUpdatedAccount((prev) => ({ ...prev, [name]: value }));
    };

    const updateEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
    }

    const handleCancelClick = () => {
        setAccountToEdit(null);
        if (setIsExpandedBubble) {
            setIsExpandedBubble(false);
        }
    };

    const saveAccount = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            const valid = validateAccount(updatedAccount);
            if (!valid) {
                setApiStatus({ code: 400, message: 'Please fill in all required fields.' });
                return;
            };
            const ownerRes = await dashboardApiV3.searchUsersByEmail(email, false);
            const owner = ownerRes?.data as UserV3;
            if (!owner) {
                setApiStatus({ code: 404, message: 'Owner not found' });
                return;
            }
            const accountToEdit = { ...updatedAccount, ownerId: owner.id } as B2bAccount;
            const response = creatingNewAccount ? await dashboardApiV3.createB2bAccount(accountToEdit) : await dashboardApiV3.updateAccount(accountToEdit);
            setApiStatus({ code: response.status, message: response.data.message });
        } catch (error: any) {
            setApiStatus({ code: error?.response?.status, message: error?.response?.data?.message });
        } finally {
            setAccountToEdit(null);
        }
    };

    const validateAccount = (account: Partial<B2bAccount>): boolean => {
        return !!account.name?.trim();
    };

    return <div className="main-dashboard-container mb-s pd-m">
        <form onSubmit={saveAccount}>
            <div className="three-columns-expand-three mb-m">
                <label htmlFor="name" className="bold reward-label-width">Name</label>
                <input
                    id="name"
                    name="name"
                    type="text"
                    value={updatedAccount.name}
                    onChange={handleChange}
                    className="bold pd-s wide-input-width"
                />
                <div />
            </div>
            <div className="three-columns-expand-three mb-m">
                <label htmlFor="type" className="bold reward-label-width">Type</label>
                <select
                    id="type"
                    name="type"
                    value={updatedAccount.type || ''}
                    onChange={handleChange}
                    className="bold pd-s wide-input-width"
                >
                    <option value="" disabled>Select type</option>
                    {
                        Object.values(AccountType).map(
                            (type) => (
                                <option key={type} value={type}>{type}</option>
                            )
                        )
                    }
                </select>
                <div />
            </div>
            <div className="three-columns-expand-three mb-m">
                <label htmlFor="email" className="bold reward-label-width">Owner Email</label>
                <input
                    id="owneremail"
                    name="owneremail"
                    type="text"
                    value={email}
                    onChange={updateEmail}
                    className="bold pd-s wide-input-width"
                />
                <div />
            </div>
            <div className="three-columns-expand-three mb-m">
                <label htmlFor="paymentMode" className="bold reward-label-width">Payment Mode</label>
                <select
                    id="paymentMode"
                    name="paymentMode"
                    value={updatedAccount.paymentMode || ''}
                    onChange={handleChange}
                    className="bold pd-s wide-input-width"
                >
                    <option value="" disabled>Select payment mode</option>
                    {
                        Object.values(AccountPaymentMode).map(
                            (mode) => (
                                <option key={mode} value={mode}>{mode}</option>
                            )
                        )
                    }
                </select>
                <div />
            </div>
            <div className="four-columns-expand-one-four">
                <div />
                <div className="three-columns-expand-one-three mt-m">
                    <div />
                    <button type="button" className="center-wrapper bold pagination-button white-text mb-s pd-s" onClick={handleCancelClick}>
                        CANCEL
                    </button>
                    <div />
                </div>
                <div className="three-columns-expand-one-three mt-m ml-m">
                    <div />
                    <button type="submit" className="center-wrapper bold pagination-button white-text mb-s pd-s">
                        SAVE
                    </button>
                    <div />
                </div>
                <div />
            </div>
        </form>
    </div>
};